import { Injectable, forwardRef, Inject } from '@angular/core';
import { AdminApiHandlerService } from '../api-handler/api-handler.service';
import { environment } from '../../../../environments/environment';
import { ApiResponseDispatcher } from '../../../store/api-response/api-response-dispatcher';
import { CommonDispatcher } from '../../../store/common/common-dispatcher';
import { MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { DialogBoxComponent } from '../../../common/components/dialog-box/dialog-box.component';
import { CustomerDispatcher } from '../../../store/customer/customer-dispatcher';
import { SpinnerService } from '../../../common/components/spinner/overlay-spinner.service';
import { SpinnerRef } from '../../../common/components/spinner/overlay-ref.service';
import { SnackBarService } from '../../../common/services/snack-bar/snack-bar.service';
import { TranslationService } from '../../../common/services/languages/translation.service';
import { AppState } from '../../../store/state.model';
import { NgRedux } from '@angular-redux/store';
import { Subscription } from 'rxjs';
import { Guid } from 'guid-typescript';

@Injectable()
export class OrchestrationService {
  /**
  *@var Declaration of spinner Ref
  **/
  public subscriptionStatus = '';
  public packageSubscriptionRequest: object = {};
  public wifiRequest: object = {};
  public userNotFoundApiErrorCount = 0;
  public pncStatusData: any;
  /**
   * Survives customer-card recreation. A new search must not reuse an in-flight request id.
   */
  private smsConsentRequestSeq = 0;

  constructor(
    @Inject(forwardRef(() => AdminApiHandlerService)) public apiHandler: AdminApiHandlerService,
    public apiDispatcher: ApiResponseDispatcher,
    public commonDispatcher: CommonDispatcher,
    public customerDispatcher: CustomerDispatcher,
    public dialog: MatDialog,
    public router: Router,
    public spinner: SpinnerService,
    private snackBarService: SnackBarService,
    private translate: TranslationService,
    public ngRedux: NgRedux<AppState>,
  ) { }
  /**
  * set the api response error or success
  **/
  private setResponse(method: string, type: string, body: object, spinnerRef?: SpinnerRef) {
    if (!!spinnerRef) { spinnerRef.close(); }
    this.apiDispatcher[method](Object.assign(body, { type }));
  }
  /**
  * searchCustomer Calls API for searching the customer
  **/
  public searchCustomer(customerType, params) {
    let spinnerRef;
    let setMethod;
    switch (customerType) {
      case 'subscribed':
        setMethod = 'setSubscribedCustomer';
        break;
      case 'remote':
        setMethod = 'setRemoteCustomerResponse';
        break;
      case 'subscriptionHistoryCustomer':
        setMethod = 'setSubscriptionHistoryCustomer';
        break;
      default:
        setMethod = 'setCustomerResponse';
        break;
    }
    this.apiDispatcher[setMethod]({});
    this.apiHandler.getRequest(environment.admin.apis.customer_search, { type: 'acm-dealer', params }).subscribe({
      next: (response) => this.setResponse(setMethod, 'success', response.payload, spinnerRef),
      error: (error) => this.setResponse(setMethod, 'error', error, spinnerRef)
    });
  }

  /**
   * @function findVehicle action to find vehicle (api call)
   **/
  public findVehicle(vin: string, vinList?: string): void {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setfindVehicleResponse({});
    this.apiHandler.getRequest(environment.admin.apis.subscription.details, {
      type: 'cvs_details', params: { vin }
    }).subscribe({
      next: (response) => {
        const vinListobject: Array<any> = [];
        vinListobject.push(response['payload']['vehicle']);
        !!vinList && vinList === 'vinList' ? this.setResponse('setVinlistResponse', 'success', { payload: vinListobject }, spinnerRef) : this.setResponse('setfindVehicleResponse', 'success', response, spinnerRef);
      },
      error: (error) => this.setResponse('setfindVehicleResponse', 'error', error, spinnerRef)
    });
  }
  /**
   * @function getVehicleByVin action to get vehicle by vin
   **/
  public getVehicleByVin(vehicle: object) {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setVinlistResponse({});
    this.getVehicleByVinRequest(vehicle, spinnerRef);
  }

  /**
   * @function getVehiclesByGuid action to get vehicle by guid (api call)
   **/
  public getVehiclesByGuid(customer: object) {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setVinlistResponse({});
    this.apiHandler.getRequest(environment.admin.apis.subscription.vehicleAssociation, {
      type: 'vehicle_list', params: {
        guid: customer['guid']
      }
    }).subscribe({
      next: (res) => this.setResponse('setVinlistResponse', 'success', res, spinnerRef),
      error: (error) => this.setResponse('setVinlistResponse', 'error', error, spinnerRef)
    });
  }

  /**
  * @function lookupKey to fetch digital key info from lookupKey call
  * @argument vin vehicle identification number
  * @returns void
  */
  public lookupKey(vin: string): void {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setLookUpkeysResponse({});
    this.apiHandler.postRequest(environment.admin.apis.subscription.lookUpKey, { vin: vin },
      { type: 'digital_Key' }).subscribe({
      next: (response) => this.setResponse('setLookUpkeysResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setLookUpkeysResponse', 'error', error, spinnerRef)
    });
  }
  /**
   * @function getPreview action to get preview
   **/
  public getPreview(params): void {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setPreview({});
    this.apiHandler.getRequest(`${environment.admin.apis.subscription.preview}`, params).subscribe({
      next: (response) => {
        this.setResponse('setPreview', 'success', Object.assign({ vin: params.params.vin }, response), spinnerRef);
      },
      error: (error) => this.setResponse('setPreview', 'error', error, spinnerRef)
    });
  }
  /**
   * @function getRemoteCommand action to get remote commands (api call)
   **/
  public getRemoteCommand(params: object, generation: string): void {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setRemoteCommandResponse({});
    let url;
 
    switch (generation) {
      case '17CYPLUS':
      case '21MM':
        url = environment.admin.apis.subscription.remote;
        break;
      case 'GR86':
        url = environment.admin.apis.subscription.remote_gr86;
        break;
      case '24MM':
        url = environment.admin.apis.subscription.remote_24mm;
        break;      
      default:
        url = environment.admin.apis.subscription.remote_legacy;
    }
 
    this.apiHandler.getRequest(url, params).subscribe({
      next: (response) => this.setResponse('setRemoteCommandResponse', 'success', Object.assign({ vin: params['params'].vin }, response), spinnerRef),
      error: (error) => this.setResponse('setRemoteCommandResponse', 'error', error, spinnerRef)
    });
  }


  /**
   * @function getVehicleByVinRequest request to get vehicle by vin (api call)
   **/
  private getVehicleByVinRequest(vehicle: object, spinnerRef: SpinnerRef): void {

    this.apiHandler.getRequest(environment.admin.apis.subscription.vinlist, {
      type: 'vehicle_list', params: {
        vin: vehicle['vin'],
      }
    }).subscribe({
      next: (response) => this.setResponse('setVinlistResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setVinlistResponse', 'error', error, spinnerRef)
    });
  }

  /**
   * @function getPlugAndChargeStatus request to get PnC status by vin (api call)
   **/
  public getPlugAndChargeStatus(brand: string, vin: string, region: string): void {
    const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(`${environment.admin.apis.plug_and_charge}?cert_type=provision%2Ccontract`, {
      type: 'plug_and_charge', params: {
        vin,
        brand,
        region
      }
    }).subscribe({
      next: (response) => this.setResponse('setPncStatusResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setPncStatusResponse', 'error', error, spinnerRef)
    });
  }

  /**
    * showErrorComponent displays the error component and sets the error code
    **/
  private routeError(): void {
    this.router.navigate(['dashboard/error'], { queryParams: {}, skipLocationChange: false });
  }
  /**
   * @method showError displays the error message
   * @arg error error response from the api
   */
  public showError(error: object): void {
    if (error['errorMessage'] === 'User Not Found') {
      this.userNotFoundApiErrorCount++;
    }
    if (error['errorCode'] === 'UNAUTHORIZED') {
      this.routeError();
    } else {
      if (this.userNotFoundApiErrorCount <= 1) {
        this.commonDispatcher.setErrorCode(error['errorCode']);
        this.dialog.open(DialogBoxComponent, {
          data: {
            type: 'api-error',
            message: error['errorMessage'],
            endPoint: error['api']
          },
          width: '30em',
          height: 'auto'
        });
      }
    }
  }
  /**
   * @method updateCustomer updates customer information
   * @arg data data to update
   */
  public updateCustomer(data: object, vehiclebrands, generation) {
    const spinnerRef = this.spinner.open();
    return this.apiHandler.putRequest(environment.admin.apis.customer_update, data, {
      type: 'acm-cu',
      params: {
        generation,
        vehiclebrands
      }
    }).subscribe({
      next: (response) => this.setResponse('setUpdateCustomerResponse', 'success', response.payload, spinnerRef),
      error: (error) => this.setResponse('setUpdateCustomerResponse', 'error', error, spinnerRef)
    });
  }
  /**
   * @function subscriptionHistory action to find subscription history (api call)
   **/
  public getSubscriptionHistory(vin: string, page, limit): void {
    const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(environment.admin.apis.subscription.subscription_history, {
      type: 'subscription-history', params: { vin, page, limit }
    }).subscribe({
      next: (response) => this.setResponse('setSubscriptionHistoryResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setSubscriptionHistoryResponse', 'error', error, spinnerRef)
    }
    );
  }
  /**
   * @function getCustomerHistory action to find customer history (api call)
   **/
  public getCustomerHistory(email: string, pageNo: number, pageSize: number): void {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher['setCustomerHistoryResponse']({});
    this.apiHandler.getRequest(environment.admin.apis.customer_history, {
      type: 'customer-history', params: { email, pageNo, pageSize }
    }).subscribe({
      next: (response) => this.setResponse('setCustomerHistoryResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setCustomerHistoryResponse', 'error', error, spinnerRef)
    }
    );
  }
  /**
   * @function getWifiStatusRequest request to get vehicle wifi status by vin and guid (api call)
   **/
  public getWifiStatusRequest(vehicle: object): void {
    //const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(environment.admin.apis.subscription.attWifiStatus, {
      type: 'att-wifi-status', params: {
        vin: vehicle['vin']
      }
    }).subscribe({
      next: (response) => this.setResponse('setWifiStatusResponse', 'success', response, null),
      error: (error) => this.setResponse('setWifiStatusResponse', 'error', error, null)
    });
  }

  /**
   * @function deleteAccount request to delete account
   */
  public deleteAccount(params: object): any {
    const deleteAccountEndPoint = environment.admin.apis.delete_account;

    return this.apiHandler.deleteRequest(deleteAccountEndPoint,
      { customer: Object.assign({ userID: this.ngRedux.getState().auth.email }) },
      { type: 'delete-account', params });
  }

  /**
   * @function unlinkVehicle request to unlink vehicle from account
   */
  public unlinkVehicle(body: object, params: object): any {
    return this.apiHandler.putRequest(environment.admin.apis.subscription.unLink, body, { type: 'unlink-vehicle', params });
  }

  /**
   * @function getVerifyAddress request to get address verification (api call)
   **/
  public getVerifyAddress(address: object): void {
    const spinnerRef = this.spinner.open();
    const apiSubscription: Subscription = this.apiHandler.postRequest(environment.admin.apis.subscription.verify_address, address, {
      type: 'verify-address', params: address
    }).subscribe({
      next: (response) => {
        this.setResponse('setVerifyAddressResponse', 'success', response, spinnerRef);
        apiSubscription.unsubscribe();
      },
      error: (error) => {
        this.setResponse('setVerifyAddressResponse', 'error', error, spinnerRef);
        apiSubscription.unsubscribe();
      }
    });
  }

  /**
   * @function getProvisioningRequest request to get vehicle provisioning status by guid (api call)
   **/
  public getProvisioningRequest(vin: string): void {
    const spinnerRef = this.spinner.open();
    if (vin) {
      this.apiHandler.getRequest(environment.admin.apis.subscription.getProvisioning, {
        type: 'get-provisioning', params: {
          vin
        }
      }).subscribe({
        next: (response) => this.setResponse('setGetProvisioningResponse', 'success', response, spinnerRef),
        error: (error) => this.setResponse('setGetProvisioningResponse', 'error', error, spinnerRef)
      });
    } else {
      spinnerRef.close();
    }
  }
  /**
   * @method retrigerProvisioning sends vehicle information to retriger provisioning
   */
  public retrigerProvisioning(vin: string, subscriberGuid: string, brand: string, type: string) {
    let endPoint;
    const spinnerRef = this.spinner.open();
    switch (type) {
      case 'dcm':
      case 'remote':
        endPoint = environment.admin.apis.subscription.retrigerProvisioningRemote;
        break;
      case 'service':
        endPoint = environment.admin.apis.subscription.retrigerProvisioningService;
        break;
      case 'headunit':
        endPoint = environment.admin.apis.subscription.retrigerProvisioningHeadunit;
        break;
      case 'safety':
        endPoint = environment.admin.apis.subscription.retrigerProvisioningSafety;
        break;
      case 'package':
        endPoint = environment.admin.apis.subscription.retrigerProvisioningPackage;
        break;
    }
    this.apiHandler.postRequest(endPoint, { vin, subscriberGuid }, {
      type: 'retriger-provisioning', params: { brand }
    }).subscribe({
      next: (response) => this.setResponse('setRetrigerProvisioningResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setRetrigerProvisioningResponse', 'error', error, spinnerRef)
    });
  }

  /**
   * @method updateEmergencyContact updates customer information
   * @arg data data to update
   */
  public updateEmergencyContact(data: object, vehicleDetails: object) {
    const endPoint = environment.admin.apis.subscription.emergencyContact;
    const spinnerRef = this.spinner.open();

    return this.apiHandler.putRequest(endPoint, data, {
      type: 'update-emergency-contact',
      params: {
        brand: vehicleDetails['brand'],
        generation: vehicleDetails['generation'],
        region: vehicleDetails['region']
      }
    }).subscribe({
      next: (response) => this.setResponse('setUpdateEmergencyContactResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setUpdateEmergencyContactResponse', 'error', error, spinnerRef)
    });
  }
  /**
 * @method getEmergencyContact fetcyes the emergency contact details
 * @arg data data to update
 */
  public getEmergencyContact(vehicle: object) {
    const endPoint = environment.admin.apis.subscription.emergencyContact;
    const spinnerRef = this.spinner.open();
    return this.apiHandler.getRequest(endPoint, {
      type: 'emergency-contact',
      params: {
        vin: vehicle['vin'],
        guid: vehicle['subscriberGuid'],
        region: vehicle['region'],
        brand: vehicle['brand'],
        generation: vehicle['generation'],
      }
    }).subscribe({
      next: (response) => this.setResponse('setEmergencyContactResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setEmergencyContactResponse', 'error', error, spinnerRef)
    });
  }
  /**
   * @method getZuoraCustomer gets the customer information from zuora
   * @arg data data to update
   */
  public getZuoraCustomer(guid) {
    const endPoint = environment.admin.apis.subscription.zuora;
    const spinnerRef = this.spinner.open();

    return this.apiHandler.getRequest(endPoint, {
      type: 'get-zuora-information',
      params: {
        guid
      }
    }).subscribe({
      next: (response) => this.setResponse('setZuoraResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setZuoraResponse', 'error', error, spinnerRef)
    });
  }
  /**
 * @function getProvisionHistory action to find Provision history (api call)
 **/
  public getProvisionHistory(vin: string, page: number = 1, limit: number = 10): void {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher['setProvisionHistoryResponse']({});
    this.apiHandler.getRequest(environment.admin.apis.subscription.provision_history, {
      type: 'provision-history', params: { vin, page, limit }
    }).subscribe({
      next: (response) => this.setResponse('setProvisionHistoryResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setProvisionHistoryResponse', 'error', error, spinnerRef)
    }
    );
  }

  // /**
  //  * @method getRequestGroupFields gets the group fields from jira service desk
  //  */
  // public getRequestGroupFields(requestId) {
  //   const endPoint = environment.admin.apis.jiraServiceDesk.requestGroupFields;
  //   const spinnerRef = this.spinner.open();

  //   this.apiHandler.getRequest(endPoint, {
  //     type: 'cms-request-group-fields',
  //     params: {
  //       requestId
  //     }
  //   }).subscribe(
  //     (response) => this.setResponse('setCmsRequestGroupFieldsResponse', 'success', response, spinnerRef),
  //     (error) => this.setResponse('setCmsRequestGroupFieldsResponse', 'error', error, spinnerRef));
  // }

  // /**
  //  * @method cmsAttachFile attach a file to jira service desk
  //  */
  // public cmsAttachFile(file) {
  //   const endPoint = environment.admin.apis.jiraServiceDesk.getServiceDeskId;
  //   const spinnerRef = this.spinner.open();

  //   this.apiHandler.postRequest(endPoint, file, {
  //     type: 'cms-attach-file',
  //   }).subscribe(
  //     (response) => this.setResponse('setCmsAttachFile', 'success', response, spinnerRef),
  //     (error) => this.setResponse('setCmsAttachFile', 'error', error, spinnerRef));
  // }
  /**
   * @method cmsCreateTicket creates a ticket on jira service desk
   */
  public cmsCreateTicket(ticketInfo, attachment) {
    const endPoint = environment.admin.apis.jiraServiceDesk.createTicket;
    const spinnerRef = this.spinner.open();

    this.apiHandler.postRequest(endPoint, ticketInfo, {
      type: 'cms-create-ticket',
      params: {
        attachment
      }
    }).subscribe({
      next: (response) => this.setResponse('setCmsCreateTicketResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setCmsCreateTicketResponse', 'error', error, spinnerRef)
    });
  }


  /**
   * @method getTss gets the telematics services status
   */
  public getTss() {
    const endPoint = environment.admin.apis.tss;
    const spinnerRef = this.spinner.open();

    this.apiHandler.getRequest(endPoint, {
      type: 'tss-request'
    }).subscribe({
      next: (response) => this.setResponse('setTssResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setTssResponse', 'error', error, spinnerRef)
    });
  }
  /**
 * @method getPhoneConfigId gets the phone ConfigId of the vehicle
 */
  public getPhoneConfigId(vehicle: object) {
    const spinnerRef = this.spinner.open();
    this.apiHandler.postRequest(environment.admin.apis.phoneComapatibilities, vehicle, {
      type: 'phone-compatibility'
    }).subscribe({
      next: (response) => this.setResponse('setPhoneConfig', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setPhoneConfig', 'error', error, spinnerRef)
    });
  }
  /**
* @method getPhoneCompatibilities gets the phone compatabilities of the vehicle
*/
  public getPhoneCompatibilities(params: object) {
    const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(environment.admin.apis.phoneComapatibilities, {
      type: 'phone-compatibility',
      params: params
    }).subscribe({
      next: (response) => this.setResponse('setPhoneCompatibilities', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setPhoneCompatibilities', 'error', error, spinnerRef)
    });
  }

  /**
   * @function getGuidStatus request to get guid status
   **/
  public getGuidStatus(email: string): void {
    //const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(environment.admin.apis.guid_status, {
      type: 'guid-status', params: { email }
    }).subscribe({
      next: (response) => this.setResponse('setGuidStatus', 'success', response, null),
      error: (error) => this.setResponse('setGuidStatus', 'error', error, null)
    });
  }

  /**
   * @function getDealerDetails request to get preferred and home dealer details
   **/
  public getDealerDetails(vin: string, guid: string, region: string, brand: string, dealerName: string): void {
    let dealerType;
    let setMethod;
    let apiUrl;
    switch (dealerName) {
      case 'home-dealer':
        dealerType = 'home-dealer';
        setMethod = 'setHomeDealer';
        apiUrl = environment.admin.apis.subscription.homeDealer;
        break;
      case 'preferred-dealer':
        dealerType = 'preferred-dealer';
        setMethod = 'setPreferredDealer';
        apiUrl = environment.admin.apis.subscription.preferredDealer;
        break;
    }
    if (dealerName === 'preferred-dealer') {
      this.apiDispatcher.setPreferredDealer({});
    }
    // const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(apiUrl, {
      type: dealerType, params: { vin, guid, region, brand }
    }).subscribe({
      next: (response) => {
        const payload = response && response.payload;
        const body = dealerName === 'preferred-dealer'
          ? this.buildPreferredDealerBody(payload, vin, guid)
          : payload;
        this.setResponse(setMethod, 'success', body, null);
      },
      error: (error) => {
        const body = dealerName === 'preferred-dealer'
          ? Object.assign({}, error, { requestVin: vin, requestGuid: guid })
          : error;
        this.setResponse(setMethod, 'error', body, null);
      }
    });
  }

  /**
   * Tag the preferred dealer payload with the VIN and guid that were requested
   * so a response for a previously selected vehicle cannot be shown for the current one.
   */
  private buildPreferredDealerBody(payload: any, vin: string, guid: string): object {
    const body = Array.isArray(payload) ? payload.slice() : Object.assign({}, payload || {});
    body.requestVin = vin;
    body.requestGuid = guid;
    return body;
  }

  /**
   * @function getOutageBannerMessage request to get the outage banner message
   **/
  public getOutageBannerMessage(): void {
    const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(environment.admin.apis.subscription.outageBanner, {
      type: 'outage-banner'
    }).subscribe({
      next: (response) => this.setResponse('setOutageBannerMessage', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setOutageBannerMessage', 'error', error, spinnerRef)
    });
  }

  /**
   * @function getVehicleHistory request to get the vehicle history
   **/
  public getVehicleHistory(vin: string): void {
    const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(environment.admin.apis.subscription.vehicleHistory + vin, {
      type: 'vehicle-history', params: { vin }
    }).subscribe({
      next: (response) => this.setResponse('setVehicleHistory', 'success', response.payload, spinnerRef),
      error: (error) => {
        // The ApiHandler throws an object with errorCode (often array) + errorMessage
        const codes = ([] as any[]).concat(error && error.errorCode || []);
        const msg = (error && error.errorMessage || '').toString().toLowerCase();
        const isNoData = codes.includes(404) || codes.includes('404') || codes.includes('CVS-0002') || msg.includes('requested data not found');
        if (isNoData) {
        // Normalize "no records" to success with empty payload
          this.setResponse('setVehicleHistory', 'success', { vehicleEventHistoryData: [] }, spinnerRef);
          } 
          else 
            {
              this.setResponse('setVehicleHistory', 'error', error, spinnerRef);
            }
      }
    });
  }

  /**
   * @function getLinkedAccounts get linked accounts
   **/
  public getLinkedAccounts(vin: string): void {
    const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(environment.admin.apis.subscription.linkedAccounts, {
      type: 'linked-accounts-21mm', params: { vin }
    }).subscribe({
      next: (response) => this.setResponse('setLinkedAccounts', 'success', { payload: response.payload }, spinnerRef),
      error: (error) => this.setResponse('setLinkedAccounts', 'error', error, spinnerRef)
    });
  }

  /**
   * @function getProductPackages request to get the product packages
   **/
  public getProductPackages(vehicleInfo): void {
    const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(environment.admin.apis.subscription.productPackages, {
      type: 'product-packages', params: {
        asicode: vehicleInfo['asiCode'],
        hwType: vehicleInfo['hwType'],
        generation: vehicleInfo['generation'],
        region: vehicleInfo['region'],
        brand: vehicleInfo['brand'] === 'T' ? 'Toyota' : vehicleInfo['brand'] === 'L' ? 'Lexus' : vehicleInfo['brand'] === 'S' ? 'Subaru' : '',
        vin: vehicleInfo['vin'],
        guid: vehicleInfo['guid']
      }
    }).subscribe({
      next: (res) => this.setResponse('setPackageProducts', 'success', res, spinnerRef),
      error: (error) => this.setResponse('setPackageProducts', 'error', error, spinnerRef)
    });
  }

  /**
   * @function getTrialLengths request to get default trial lengths
   **/
  public getTrialLengths(vehicleInfo): void {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setTrialLengthsResponse({});
    this.apiHandler.getRequest(environment.admin.apis.subscription.trialLengths, {
      type: 'trial-lengths', params: {
        asiCode: vehicleInfo['asiCode'],
        generation: vehicleInfo['generation'],
        hardwareType: vehicleInfo['hwType'] || vehicleInfo['hardwareType'],
        region: vehicleInfo['region'],
        brand: vehicleInfo['brand'],
        huVersion: vehicleInfo['headUnit']?.['huVersion'],
        dcmModelYear: vehicleInfo['dcm']?.['dcmModelYear'],
        modelYear: vehicleInfo['model']?.['modelYear'] ?? vehicleInfo['modelYear']
      }
    }).subscribe({
      next: (response) => this.setResponse('setTrialLengthsResponse', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setTrialLengthsResponse', 'error', error, spinnerRef)
    });
  }

  /**
   * @function get21MMUserProfileSettings request to get the 21MM user profile settings
   **/
  public get21MMUserProfileSettings(vehicleInfo): void {
    const spinnerRef = this.spinner.open();
    this.apiHandler.getRequest(environment.admin.apis.subscription.userProfileSettings, {
      type: 'userprofile-21mm',
      params: {
        brand: vehicleInfo['brand'] === 'T' ? 'Toyota' : vehicleInfo['brand'] === 'L' ? 'Lexus' : vehicleInfo['brand'] === 'S' ? 'Subaru' : '',
        vin: vehicleInfo['vin'],
      }
    }
    ).subscribe({
      next: (res) => this.setResponse('set21MMUserProfileSettings', 'success', res, spinnerRef),
      error: (error) => this.setResponse('set21MMUserProfileSettings', 'error', error, spinnerRef)
    });
  }


  /**
   * @function getGoodwillPackages action to get the Goodwill packages
   **/
  public getGoodwillPackages(vehicleInfo): void {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.setGoodwillPackages({});
    this.apiHandler.getRequest(environment.admin.apis.subscription.getGoodwillPackages, {
      type: 'give-goodwill', params: {
        asicode: vehicleInfo['asiCode'],
        hwType: vehicleInfo['hwType'],
        generation: vehicleInfo['generation'],
        region: vehicleInfo['region'],
        brand: vehicleInfo['brand'],
        vin: vehicleInfo['vin'],
        guid: vehicleInfo['subscriberGuid']
      }
    }).subscribe({
      next: (response) => this.setResponse('setGoodwillPackages', 'success', response, spinnerRef),
      error: (error) => this.setResponse('setGoodwillPackages', 'error', error, spinnerRef)
    });
  }

  /**
   * @function submitGoodwillPackages action to submit the selected subscriptions for adding Goodwill
   **/
  public submitGoodwillPackages(requestPayload, vehicleInfo): void {
    const spinnerRef = this.spinner.open();
    this.apiDispatcher.submitGoodwillPackages({});
    this.apiHandler.postRequest(environment.admin.apis.subscription.submitGoodwillPackages, requestPayload, {
      type: 'submit-goodwill', params: {
        brand: vehicleInfo['brand'],
      }
    }).subscribe({
      next: (response) => this.setResponse('submitGoodwillPackages', 'success', response, spinnerRef),
      error: (error) => this.setResponse('submitGoodwillPackages', 'error', error, spinnerRef)
    });
  }

  /**
   * @function getDataConsents action to get the data consents from the privacy portal
   **/
  public getDataConsents(vehicle: object): void {
    //const spinnerRef = this.spinner.open();
    this.apiDispatcher.getDataConsents({});
    this.apiHandler.getRequest(environment.admin.apis.subscription.getDataConsents,
       {
      type: 'data-consents', params: {
        vin: vehicle['vin'],
        brand: vehicle['brand'],
        guid: vehicle['subscriberGuid']
      }
    }).subscribe({
      next: (response) => {
        if (!!response) {
          this.apiDispatcher.getDataConsents(response);
          this.setResponse('getDataConsents', 'success', response, null);
        } else {
          this.setResponse('getDataConsents', 'success', {}, null);
        }
      },
      error: (error) => this.setResponse('getDataConsents', 'error', error, null)
    });
  }
  
  /**
   * @function getAccountSmsConsent reads SMS status from GET /v1/portal/consent/account.
   * The portal service resolves consent id B2 and returns Enabled or Disabled.
   * Brand must be Toyota or Lexus. VIN is not part of this request.
   **/
  public getAccountSmsConsent(account: object): string {
    const guid = account && (account['guid'] || account['subscriberGuid'])
      ? String(account['guid'] || account['subscriberGuid']).trim()
      : '';
    const brand = this.smsConsentBrand(account && (account['brand'] || account['brandCode']));
    if (!guid || !brand) {
      return '';
    }
    this.smsConsentRequestSeq += 1;
    const requestKey = String(this.smsConsentRequestSeq);
    this.apiDispatcher.getSmsConsent({});
    this.apiHandler.getRequest(environment.admin.apis.subscription.getAccountSmsConsent, {
      type: 'sms-consent', params: {
        guid,
        brand,
        locale: 'en-US'
      }
    }).subscribe({
      next: (response) => {
        const body: { requestKey: string; payload?: object } = { requestKey };
        if (response?.payload) {
          body.payload = response.payload;
        }
        this.setResponse('getSmsConsent', 'success', body, null);
      },
      error: () => {
        this.setResponse('getSmsConsent', 'error', { requestKey }, null);
      }
    });
    return requestKey;
  }

  /**
   * Account SMS consent accepts Toyota or Lexus only. The portal API rejects other brands.
   */
  private smsConsentBrand(brand: string): string {
    const value = (brand || '').toString().trim().toUpperCase();
    if (value === 'TOY' || value === 'T' || value === 'TOYOTA') {
      return 'T';
    }
    if (value === 'LEX' || value === 'L' || value === 'LEXUS') {
      return 'L';
    }
    return '';
  }
  public getHuCompany(params, callback: Function) {
    const spinnerRef = this.spinner.open();
    return this.apiHandler.getRequest(environment.admin.apis.subscription.hucompany, {
      type: 'hu-company', params
    }).subscribe({
      next: (response) => {
        this.setResponse('getHuCompany', 'success', response, spinnerRef);
        callback(response);
      },
      error: (error) => this.setResponse('getHuCompany', 'error', error, spinnerRef)
    });

  }

  /**
   * @method updateRemoteUser updates the remote user details
   * @arg data data to update
   */
  public updateRemoteUser(data: object, params: object) {
    const endPoint = environment.admin.apis.subscription.updateRemoteUser;
    const spinnerRef = this.spinner.open();
    return this.apiHandler.putRequest(endPoint, data, {
      type: 'update-remote-user', params
    }).subscribe({
      next: (response) => this.setResponse('updateRemoteUser', 'success', response, spinnerRef),
      error: (error) => this.setResponse('updateRemoteUser', 'error', error, spinnerRef)
    });
  }

  /**
  * @function addDCMActivation  dcm activation api call.
  **/
  public addDCMActivation(vehicle: object) {
    const spinnerRef = this.spinner.open();

    this.apiDispatcher.addDCMActivation({});
    const requestPayload = { vin: vehicle['vin'] };
    this.apiHandler.putRequest(environment.admin.apis.dcmactivation, requestPayload, {
      type: 'dcm-activation', params: {
      }
    }).subscribe({
      next: (response) => {
        if (!!response) {
          this.setResponse('addDCMActivation', 'success', response, spinnerRef);
        } else {
          this.setResponse('addDCMActivation', 'success', {}, spinnerRef);
        }
        this.getProvisioningRequest(vehicle['vin']);
      },
      error: (error) => this.setResponse('addDCMActivation', 'error', error, spinnerRef)
    });
  }
    
    /**
   * @function getSurvivorDetails action to get Survivor acount details by vin (api call)
   **/
    public getSurvivorDetails(vehicle: object) {
      const spinnerRef = this.spinner.open();
      this.apiHandler.getRequest(environment.admin.apis.survivor_account, {
        type: 'survivor-account', params: {
          vin: vehicle['vin'],
          brand: vehicle['brandCode']
        }
      }).subscribe({
        next: (res) => this.setResponse('setSurvivorAccountResponse', 'success', res, spinnerRef),
        error: (error) => this.setResponse('setSurvivorAccountResponse', 'error', error, spinnerRef)
      });
    }
}


